import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  signIn,
  getMyInfo,
  loadAuth,
  logout,
  refreshToken,
  signUp,
  // refreshAccessToken,
  // signInWithGoogle,
  requestPasswordReset,
  verifyPasswordResetRequest,
  resetPassword,
  checkEmail,
  signInWithGoogle,
  requestPhoneNumberOtp,
  signInWithPhoneNumber,
  checkB2BLoginOption,
  signInWithB2B,
  changePassword,
  signInWithB2BByToken,
  setOnboardingCompleted,
  registerToBusinessGroup,
  getLinkedAccounts,
  updateUsername,
  changeEmail,
  linkGoogle,
  checkPhoneNumber,
  requestDeleteAccount,
  setMyEmailPassword,
  changMyPassword,
  linkApple,
  signInWithApple,
  setPasswordByInvitationToken,
} from "./thunks";
import { AuthState, User } from "./types";
import { Server } from "@wajeez/wajeez.microservices";

export const name = "auth";

const initialState: AuthState = {
  user: {} as User,
  tokens: {},
  isAccountDeleted: false,
  emailExist: null,
  status: "idle",
  validateEmailStatus: "idle",
  checkB2BLoginOption: {
    data: null,
    status: "idle",
  },
  b2BLogin: { data: null },
  changePasswordStatus: "idle",
  requestPasswordResetStatus: "idle",
  verifyPasswordResetRequestStatus: "idle",
  resetPasswordStatus: "idle",
  emailExistStatus: "idle",
  requestPhoneNumberOtpStatus: "idle",
  setOnboardingCompletedStatus: "idle",
  registerToBusinessGroupStatus: "idle",
  setPasswordByInvitationTokenStatus: "idle",
  isContentPrepareLoading: false,
  linkedProviders: { data: null, status: "idle" },
  updateUserNameStatus: "idle",
  updateEmailStatus: "idle",
  linkAppleStatus: "idle",
  linkGoogleStatus: "idle",
  requestDeleteAccountStatus: "idle",
  checkPhoneNumberStatus: "idle",
  setMyEmailPasswordStatus: "idle",
  changMyPasswordStatus: "idle",
};

const asyncActions = {
  signInAsync: createAsyncThunk(`${name}/signIn`, signIn),
  signUpAsync: createAsyncThunk(`${name}/signUp`, signUp),
  signInWithGoogleAsync: createAsyncThunk(
    `${name}/signInWithGoogle`,
    signInWithGoogle
  ),
  requestPhoneNumberOtpAsync: createAsyncThunk(
    `${name}/requestPhoneNumberOtp`,
    requestPhoneNumberOtp
  ),
  signInWithPhoneNumberAsync: createAsyncThunk(
    `${name}/signInWithPhoneNumber`,
    signInWithPhoneNumber
  ),
  signInWithB2BAsync: createAsyncThunk(`${name}/signInWithB2B`, signInWithB2B),
  signInWithB2BByTokenAsync: createAsyncThunk(
    `${name}/signInWithB2BByToken`,
    signInWithB2BByToken
  ),
  signInWithAppleAsync: createAsyncThunk(
    `${name}/signInWithApple`,
    signInWithApple
  ),
  // verifyOTPAsync: createAsyncThunk(`${name}/signIn`, verifyOTP),
  // sendOTPAsync: createAsyncThunk(`${name}/sendOTP`, sendOTP),
  getMyInfoAsync: createAsyncThunk(`${name}/getMyInfo`, getMyInfo),
  // updateMyInfoAsync: createAsyncThunk(`${name}/updateMyInfo`, updateMyInfo),
  loadAuthAsync: createAsyncThunk(`${name}/loadAuth`, loadAuth),
  refreshTokenAsync: createAsyncThunk(`${name}/refreshToken`, refreshToken),
  logoutAsync: createAsyncThunk(`${name}/logout`, logout),
  checkB2BLoginOptionAsync: createAsyncThunk(
    `${name}/checkB2BLoginOption`,
    checkB2BLoginOption
  ),
  changePasswordAsync: createAsyncThunk(
    `${name}/changePassword`,
    changePassword
  ),
  requestPasswordResetAsync: createAsyncThunk(
    `${name}/requestPasswordReset`,
    requestPasswordReset
  ),
  verifyPasswordResetRequestAsync: createAsyncThunk(
    `${name}/verifyPasswordResetRequest`,
    verifyPasswordResetRequest
  ),
  resetPasswordAsync: createAsyncThunk(`${name}/resetPassword`, resetPassword),
  checkEmailAsync: createAsyncThunk(`${name}/checkEmail`, checkEmail),
  setOnboardingCompletedAsync: createAsyncThunk(
    `${name}/setOnboardingCompleted`,
    setOnboardingCompleted
  ),
  registerToBusinessGroupAsync: createAsyncThunk(
    `${name}/registerToBusinessGroup`,
    registerToBusinessGroup
  ),
  setPasswordByInvitationTokenAsync: createAsyncThunk(
    `${name}/setPasswordByInvitationToken`,
    setPasswordByInvitationToken
  ),
  getLinkedAccountsAsync: createAsyncThunk(
    `${name}/auth-providers`,
    getLinkedAccounts
  ),
  updateUsernameAsync: createAsyncThunk(`${name}/update-name`, updateUsername),
  changeEmailAsync: createAsyncThunk(`${name}/change-email`, changeEmail),
  linkGoogleAsync: createAsyncThunk(`${name}/link-google`, linkGoogle),
  linkAppleAsync: createAsyncThunk(`${name}/link-apple`, linkApple),
  requestDeleteAccountrAsync: createAsyncThunk(
    `${name}/request-delete-account`,
    requestDeleteAccount
  ),
  setMyEmailPasswordAsync: createAsyncThunk(
    `${name}/set-my-email-password`,
    setMyEmailPassword
  ),
  changMyPasswordAsync: createAsyncThunk(
    `${name}/change-my-password`,
    changMyPassword
  ),
  checkPhoneNumberAsync: createAsyncThunk(
    `${name}/check-phone-number`,
    checkPhoneNumber
  ),
};

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: (_) => {
      return initialState;
    },
    clearEmailExist: (state) => {
      state.emailExist = null;
    },
    clearCheckB2BLoginOption: (state) => {
      state.checkB2BLoginOption = {
        data: null,
        status: "idle",
      };
    },
    setUser: (state, action: PayloadAction<Server.AuthenticateOutput>) => {
      state.user = action.payload;
    },
    setTokens: (state, action: PayloadAction<Server.AuthenticateOutput>) => {
      state.tokens = action.payload;
    },

    setIsContentPrepareLoading: (state, action: PayloadAction<boolean>) => {
      state.isContentPrepareLoading = action.payload;
    },
    resetSuccessAlert: (state) => {
      state.updateUserNameStatus = "idle";
      state.updateEmailStatus = "idle";
      state.linkGoogleStatus = "idle";
      state.linkAppleStatus = "idle";
      state.setMyEmailPasswordStatus = "idle";
      state.changMyPasswordStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.signInAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.signInAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.tokens = action.payload;
        state.user = action.payload;
      })
      .addCase(asyncActions.signInAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.signUpAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.signUpAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.tokens = action.payload;
        state.user = action.payload;
      })
      .addCase(asyncActions.signUpAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.signInWithAppleAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.signInWithAppleAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.tokens = action.payload;
        state.user = action.payload;
      })
      .addCase(asyncActions.signInWithAppleAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.signInWithGoogleAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        asyncActions.signInWithGoogleAsync.fulfilled,
        (state, action) => {
          state.status = "idle";
          state.tokens = action.payload;
          state.user = action.payload;
        }
      )
      .addCase(asyncActions.signInWithGoogleAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.requestPhoneNumberOtpAsync.pending, (state) => {
        state.requestPhoneNumberOtpStatus = "loading";
      })
      .addCase(asyncActions.requestPhoneNumberOtpAsync.fulfilled, (state) => {
        state.requestPhoneNumberOtpStatus = "idle";
      })
      .addCase(
        asyncActions.requestPhoneNumberOtpAsync.rejected,
        (state, action) => {
          state.requestPhoneNumberOtpStatus = "failed";
        }
      )
      .addCase(asyncActions.signInWithPhoneNumberAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        asyncActions.signInWithPhoneNumberAsync.fulfilled,
        (state, action) => {
          state.status = "idle";
          state.tokens = action.payload;
          state.user = action.payload;
        }
      )
      .addCase(
        asyncActions.signInWithPhoneNumberAsync.rejected,
        (state, action) => {
          state.status = "failed";
        }
      )
      .addCase(asyncActions.signInWithB2BAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.signInWithB2BAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.b2BLogin.data = action.payload;
      })
      .addCase(asyncActions.signInWithB2BAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.signInWithB2BByTokenAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        asyncActions.signInWithB2BByTokenAsync.fulfilled,
        (state, action) => {
          state.status = "idle";
          state.tokens = action.payload;
          state.user = action.payload;
        }
      )
      .addCase(
        asyncActions.signInWithB2BByTokenAsync.rejected,
        (state, action) => {
          state.status = "failed";
        }
      )
      .addCase(asyncActions.loadAuthAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.loadAuthAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.tokens = {
          token: action.payload.token,
          refreshToken: action.payload.refreshToken,
          statefullToken: action.payload.statefullToken,
        };
        state.user.onboardingCompleted = action.payload.onboardingCompleted;
      })
      .addCase(asyncActions.loadAuthAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(asyncActions.getMyInfoAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.getMyInfoAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(asyncActions.getMyInfoAsync.rejected, (state, action) => {
        state.status = "failed";
      })

      .addCase(asyncActions.logoutAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(asyncActions.logoutAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.tokens = {};
        state.user = {};
        state.emailExist = null;
      })
      .addCase(asyncActions.logoutAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(asyncActions.checkB2BLoginOptionAsync.pending, (state) => {
        state.checkB2BLoginOption.status = "loading";
      })
      .addCase(
        asyncActions.checkB2BLoginOptionAsync.fulfilled,
        (state, action) => {
          state.checkB2BLoginOption = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.checkB2BLoginOptionAsync.rejected,
        (state, action) => {
          state.checkB2BLoginOption.status = "failed";
        }
      )
      .addCase(asyncActions.changePasswordAsync.pending, (state) => {
        state.changePasswordStatus = "loading";
      })
      .addCase(asyncActions.changePasswordAsync.fulfilled, (state, action) => {
        state.changePasswordStatus = "idle";
      })
      .addCase(asyncActions.changePasswordAsync.rejected, (state, action) => {
        state.changePasswordStatus = "failed";
      })
      .addCase(asyncActions.requestPasswordResetAsync.pending, (state) => {
        state.requestPasswordResetStatus = "loading";
      })
      .addCase(
        asyncActions.requestPasswordResetAsync.fulfilled,
        (state, action) => {
          state.requestPasswordResetStatus = "idle";
        }
      )
      .addCase(
        asyncActions.requestPasswordResetAsync.rejected,
        (state, action) => {
          state.requestPasswordResetStatus = "failed";
        }
      )
      .addCase(
        asyncActions.verifyPasswordResetRequestAsync.pending,
        (state) => {
          state.verifyPasswordResetRequestStatus = "loading";
        }
      )
      .addCase(
        asyncActions.verifyPasswordResetRequestAsync.fulfilled,
        (state, action) => {
          state.verifyPasswordResetRequestStatus = "idle";
        }
      )
      .addCase(
        asyncActions.verifyPasswordResetRequestAsync.rejected,
        (state, action) => {
          state.verifyPasswordResetRequestStatus = "failed";
        }
      )
      .addCase(asyncActions.resetPasswordAsync.pending, (state) => {
        state.resetPasswordStatus = "loading";
      })
      .addCase(asyncActions.resetPasswordAsync.fulfilled, (state, action) => {
        state.resetPasswordStatus = "idle";
      })
      .addCase(asyncActions.resetPasswordAsync.rejected, (state, action) => {
        state.resetPasswordStatus = "failed";
      })
      .addCase(asyncActions.checkEmailAsync.pending, (state) => {
        state.emailExistStatus = "loading";
      })
      .addCase(asyncActions.checkEmailAsync.fulfilled, (state, action) => {
        state.emailExist = action.payload;
        state.emailExistStatus = "idle";
      })
      .addCase(asyncActions.checkEmailAsync.rejected, (state) => {
        state.emailExistStatus = "failed";
      })
      .addCase(asyncActions.setOnboardingCompletedAsync.pending, (state) => {
        state.setOnboardingCompletedStatus = "loading";
      })
      .addCase(asyncActions.setOnboardingCompletedAsync.fulfilled, (state) => {
        state.user.onboardingCompleted = true;
        state.setOnboardingCompletedStatus = "idle";
      })
      .addCase(asyncActions.setOnboardingCompletedAsync.rejected, (state) => {
        state.setOnboardingCompletedStatus = "failed";
      })
      .addCase(asyncActions.registerToBusinessGroupAsync.pending, (state) => {
        state.registerToBusinessGroupStatus = "loading";
      })
      .addCase(asyncActions.registerToBusinessGroupAsync.fulfilled, (state) => {
        state.registerToBusinessGroupStatus = "idle";
      })
      .addCase(
        asyncActions.registerToBusinessGroupAsync.rejected,
        (state, action) => {
          state.registerToBusinessGroupStatus = "failed";
        }
      )
      .addCase(
        asyncActions.setPasswordByInvitationTokenAsync.pending,
        (state) => {
          state.setPasswordByInvitationTokenStatus = "loading";
        }
      )
      .addCase(
        asyncActions.setPasswordByInvitationTokenAsync.fulfilled,
        (state) => {
          state.setPasswordByInvitationTokenStatus = "idle";
        }
      )
      .addCase(
        asyncActions.setPasswordByInvitationTokenAsync.rejected,
        (state, action) => {
          state.setPasswordByInvitationTokenStatus = "failed";
        }
      )
      .addCase(asyncActions.getLinkedAccountsAsync.pending, (state) => {
        state.linkedProviders.status = "loading";
      })
      .addCase(
        asyncActions.getLinkedAccountsAsync.fulfilled,
        (state, action) => {
          state.linkedProviders.status = "idle";
          state.linkedProviders.data = action.payload;
        }
      )
      .addCase(
        asyncActions.getLinkedAccountsAsync.rejected,
        (state, action) => {
          state.linkedProviders.status = "failed";
        }
      )
      .addCase(asyncActions.updateUsernameAsync.pending, (state) => {
        state.updateUserNameStatus = "loading";
      })
      .addCase(asyncActions.updateUsernameAsync.fulfilled, (state) => {
        state.updateUserNameStatus = "success";
      })
      .addCase(asyncActions.updateUsernameAsync.rejected, (state) => {
        state.updateUserNameStatus = "failed";
      })
      .addCase(asyncActions.changeEmailAsync.pending, (state) => {
        state.updateEmailStatus = "loading";
      })
      .addCase(asyncActions.changeEmailAsync.fulfilled, (state) => {
        state.updateEmailStatus = "success";
      })
      .addCase(asyncActions.changeEmailAsync.rejected, (state) => {
        state.updateEmailStatus = "failed";
      })
      .addCase(asyncActions.linkGoogleAsync.pending, (state) => {
        state.linkGoogleStatus = "loading";
      })
      .addCase(asyncActions.linkGoogleAsync.fulfilled, (state) => {
        state.linkGoogleStatus = "success";
      })
      .addCase(asyncActions.linkGoogleAsync.rejected, (state) => {
        state.linkGoogleStatus = "failed";
      })
      .addCase(asyncActions.linkAppleAsync.pending, (state) => {
        state.linkAppleStatus = "loading";
      })
      .addCase(asyncActions.linkAppleAsync.fulfilled, (state) => {
        state.linkAppleStatus = "success";
      })
      .addCase(asyncActions.linkAppleAsync.rejected, (state) => {
        state.linkAppleStatus = "failed";
      })
      .addCase(asyncActions.requestDeleteAccountrAsync.pending, (state) => {
        state.requestDeleteAccountStatus = "loading";
      })
      .addCase(asyncActions.requestDeleteAccountrAsync.fulfilled, (state) => {
        state.requestDeleteAccountStatus = "idle";
      })
      .addCase(asyncActions.requestDeleteAccountrAsync.rejected, (state) => {
        state.requestDeleteAccountStatus = "failed";
      })
      .addCase(asyncActions.setMyEmailPasswordAsync.pending, (state) => {
        state.setMyEmailPasswordStatus = "loading";
      })
      .addCase(asyncActions.setMyEmailPasswordAsync.fulfilled, (state) => {
        state.setMyEmailPasswordStatus = "success";
      })
      .addCase(asyncActions.setMyEmailPasswordAsync.rejected, (state) => {
        state.setMyEmailPasswordStatus = "failed";
      })
      .addCase(asyncActions.changMyPasswordAsync.pending, (state) => {
        state.changMyPasswordStatus = "loading";
      })
      .addCase(asyncActions.changMyPasswordAsync.fulfilled, (state) => {
        state.changMyPasswordStatus = "success";
      })
      .addCase(asyncActions.changMyPasswordAsync.rejected, (state) => {
        state.changMyPasswordStatus = "failed";
      })
      .addCase(asyncActions.checkPhoneNumberAsync.pending, (state) => {
        state.checkPhoneNumberStatus = "loading";
      })
      .addCase(asyncActions.checkPhoneNumberAsync.fulfilled, (state) => {
        state.checkPhoneNumberStatus = "idle";
      })
      .addCase(asyncActions.checkPhoneNumberAsync.rejected, (state) => {
        state.checkPhoneNumberStatus = "failed";
      });
  },
});

export const AuthActions = {
  ...slice.actions,
  ...asyncActions,
};

export default slice.reducer;
